import React from 'react';
import { Calendar, Clock, User } from 'lucide-react';
import DOMPurify from 'dompurify';

const BlogPostModal = ({ post, onClose }) => {
  if (!post) return null;

  return (
    <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-sm z-50 overflow-y-auto">
      <div className="min-h-screen py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 px-4 py-2 rounded-full text-sm font-semibold">
                {post.category || 'Article'}
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white text-2xl font-bold p-2 hover:bg-slate-800 rounded-lg transition-colors"
              aria-label="Close article"
            >
              ×
            </button>
          </div>

          {/* Article Header */}
          <div className="text-center space-y-6 mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">{post.title}</h1>

            <div className="flex items-center justify-center space-x-6 text-slate-400">
              <div className="flex items-center space-x-2">
                <User size={16} />
                <span>{post.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Calendar size={16} />
                <span>{new Date(post.date).toLocaleDateString()}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock size={16} />
                <span>{post.readTime}</span>
              </div>
            </div>
          </div>

          {/* Featured Image */}
          {post.image && (
            <div className="mb-12 rounded-xl overflow-hidden">
              <img src={post.image} alt={post.title} className="w-full h-64 md:h-80 object-cover" />
            </div>
          )}

          {/* Article Content */}
          <div className="bg-white rounded-xl p-8 md:p-12">
            {post.fullContent ? (
              <div
                className="prose prose-lg max-w-none"
                // Blog HTML is curated from our internal data source, but we
                // still sanitise before injecting to prevent XSS if the
                // source is ever swapped for a CMS / user-generated field.
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(post.fullContent, {
                    USE_PROFILES: { html: true },
                  }),
                }}
              />
            ) : (
              <div className="prose prose-lg max-w-none">
                <p className="text-xl text-gray-600 mb-6">{post.excerpt}</p>
                <p className="text-gray-700 leading-relaxed">
                  This is a preview of the article. The full content would be displayed here
                  for articles that have complete content available.
                </p>
              </div>
            )}
          </div>

          {/* Back to Blog Button */}
          <div className="text-center mt-12">
            <button
              onClick={onClose}
              className="inline-flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 px-8 py-4 rounded-lg font-semibold hover:from-amber-400 hover:to-orange-400 transition-all duration-200"
            >
              <span>← Back to Blog</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPostModal;
